import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { getBook } from '../services/bookService';
import { doc, updateDoc, getDoc, serverTimestamp, onSnapshot, collection, query, where } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useAuth } from './AuthContext';
import { useDimensions } from './DimensionContext';
import { useLocation } from 'react-router-dom';

const BookContext = createContext(null);

export function BookProvider({ children }) {
  const { user } = useAuth();
  const { setCurrentBookPageSize } = useDimensions();
  const location = useLocation();
  const [currentBook, setCurrentBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [isDesignReadyForViewing, setIsDesignReadyForViewing] = useState(true);
  const [allowedProgress, setAllowedProgress] = useState(0);
  const [loadingUIMessage, setLoadingUIMessage] = useState({progress:0,message:'Initializing...'});
  const [showImageLoadingPlaceholder, setShowImageLoadingPlaceholder] = useState(false);
  // Millis of the processing doc's last update - used to detect stale/stuck design generation
  const [processingUpdatedAt, setProcessingUpdatedAt] = useState(null);
  const [pendingImageJobs, setPendingImageJobs] = useState(new Map());
  const [pendingImageJobsLoaded, setPendingImageJobsLoaded] = useState(false);
  const loadInFlightRef = useRef({ id: null, inFlight: false });
  const didReloadForGeneratingCharacterRef = useRef({});
  const currentBookRef = useRef(null);
  const mergedFieldsRef = useRef({ world: undefined, characterData: undefined });
  // Tracks the spread currently being edited on /design to prevent onSnapshot from overwriting unsaved edits
  const activeSpreadRef = useRef(null);

  const setActiveSpread = useCallback((spreadId) => {
    activeSpreadRef.current = spreadId;
  }, []);

  const loadBook = useCallback(
    async (bookId) => {
      // Prevent overlapping loads for the same book
      if (loadInFlightRef.current.inFlight && loadInFlightRef.current.id === bookId) {
        console.log('[SBW:BookContext] Skipping duplicate loadBook for', bookId);
        return;
      }
      loadInFlightRef.current = { id: bookId, inFlight: true };
      if (!user) {
        console.log('[SBW:BookContext] No user logged in');
        setError('Please log in to access books');
        loadInFlightRef.current.inFlight = false;
        return;
      }

      console.log('[SBW:BookContext] Loading book:', bookId);
      setLoading(true);
      setError(null);

      try {
        const bookData = /** @type {any} */ (await getBook(bookId));
        console.log(
          '[SBW:BookContext] Raw book data:',
          JSON.stringify(bookData, null, 2)
        );

        if (!bookData) {
          throw new Error('Book not found');
        }

        // Transform pages data into a more usable format
        const transformedPages = {};
        for (let i = 0; i < (bookData.totalPages || 32); i++) {
          transformedPages[i] = {
            elements: bookData.pages?.[i]?.elements || [],
            updatedAt: bookData.pages?.[i]?.updatedAt || new Date(),
          };
        }

        // Debug the elements array
        console.log(
          '[SBW:BookContext] Book elements:',
          bookData.elements?.map((el) => ({
            id: el.id,
            type: el.type,
            spreadId: el.spreadId,
            imagePrompt: el.imagePrompt,
          }))
        );

        // Group elements by spread for debugging
        const elementsBySpread = {};
        bookData.elements?.forEach((el) => {
          if (!elementsBySpread[el.spreadId]) {
            elementsBySpread[el.spreadId] = [];
          }
          elementsBySpread[el.spreadId].push({
            id: el.id,
            type: el.type,
            imagePrompt: el.imagePrompt,
          });
        });
        console.log(
          '[SBW:BookContext] Elements by spread:',
          JSON.stringify(elementsBySpread, null, 2)
        );

        const transformedBook = {
          ...bookData,
          pages: transformedPages,
        };

        // Targeted debug: verify world presence after fetch
        try {
          console.log('[BookContext] world data:', transformedBook?.world);
        } catch (_) {}

        console.log('[SBW:BookContext] Transformed book:', {
          id: transformedBook.id,
          totalPages: transformedBook.totalPages,
          elementCount: transformedBook.elements?.length,
          spreads: Object.keys(transformedBook.spreads || {}),
          imageElements: transformedBook.elements
            ?.filter((el) => el.type === 'image')
            ?.map((el) => ({
              id: el.id,
              spreadId: el.spreadId,
              imagePrompt: el.imagePrompt,
              orientation: el.orientation,
            })),
          spreadsDetail: Object.entries(transformedBook.spreads || {}).map(([spreadId, spread]) => ({
            spreadId,
            elements: spread?.elements?.map(el => ({
              id: el.id,
              type: el.type,
              orientation: el.orientation,
            })) || []
          }))
        });

        setCurrentBook(transformedBook);
        
        // Set the book's page size in the DimensionContext
        // This will ensure we use the correct page size for this specific book
        if (transformedBook.id && transformedBook.pageSize) {
          console.log(`[SBW:BookContext] Setting page size for book ${transformedBook.id} to ${transformedBook.pageSize}`);
          setCurrentBookPageSize(transformedBook.id, transformedBook.pageSize);
        } else {
          console.log(`[SBW:BookContext] No page size found for book ${transformedBook.id}, using default`);
        }
      } catch (err) {
        console.error('[SBW:BookContext] Error loading book:', err);
        setError(err.message);
      } finally {
        setLoading(false);
        loadInFlightRef.current.inFlight = false;
      }
    },
    [user, setCurrentBookPageSize]
  );

  const clearBook = useCallback(() => {
    console.log('[SBW:BookContext] Clearing current book');
    setCurrentBook(null);
    setError(null);
  }, []);

  const updateBook = useCallback(
    async (bookId, updates) => {
      try {
        const bookRef = doc(db, 'books', bookId);

        // Get current state before update
        const currentDoc = await getDoc(bookRef);
        const currentData = currentDoc.data();

        // Add updatedAt timestamp
        const updatesWithTimestamp = {
          ...updates,
          updatedAt: serverTimestamp(),
        };

        // Log detailed request data
        console.log('=== UPDATE DETAILS ===');
        console.log('1. Current state in Firebase:', {
          bookId,
          spreads: Object.entries(currentData?.spreads || {}).map(
            ([spreadId, spread]) => ({
              spreadId,
              elementCount: spread?.elements?.length || 0,
              elementIds: spread?.elements?.map((el) => el.id) || [],
            })
          ),
        });

        console.log('2. Update being sent:', {
          bookId,
          spreads: Object.entries(updates.spreads || {}).map(
            ([spreadId, spread]) => ({
              spreadId,
              elementCount: spread?.elements?.length || 0,
              elementIds: spread?.elements?.map((el) => el.id) || [],
              elements: spread?.elements?.map((el) => ({
                id: el.id,
                type: el.type,
                content: el.content,
              })),
            })
          ),
        });

        // Perform update
        await updateDoc(bookRef, updatesWithTimestamp);

        // Fetch updated document
        const updatedDoc = await getDoc(bookRef);
        const updatedData = updatedDoc.data();

        // Log detailed response data
        console.log('3. New state in Firebase:', {
          bookId,
          spreads: Object.entries(updatedData?.spreads || {}).map(
            ([spreadId, spread]) => ({
              spreadId,
              elementCount: spread?.elements?.length || 0,
              elementIds: spread?.elements?.map((el) => el.id) || [],
            })
          ),
        });

        // Compare element counts
        const diffReport = {};
        Object.keys(updates.spreads || {}).forEach((spreadId) => {
          const beforeCount =
            currentData?.spreads?.[spreadId]?.elements?.length || 0;
          const updateCount = updates.spreads[spreadId]?.elements?.length || 0;
          const afterCount =
            updatedData?.spreads?.[spreadId]?.elements?.length || 0;

          if (beforeCount !== afterCount || updateCount !== afterCount) {
            diffReport[spreadId] = {
              beforeCount,
              updateCount,
              afterCount,
              beforeIds:
                currentData?.spreads?.[spreadId]?.elements?.map(
                  (el) => el.id
                ) || [],
              updateIds:
                updates.spreads[spreadId]?.elements?.map((el) => el.id) || [],
              afterIds:
                updatedData?.spreads?.[spreadId]?.elements?.map(
                  (el) => el.id
                ) || [],
            };
          }
        });

        if (Object.keys(diffReport).length > 0) {
          console.log('4. !!! Element count mismatches detected:', diffReport);
        } else {
          console.log('4. !!! No element count mismatches detected.');
        }

        // Update local state
        setCurrentBook((prev) => {
          const updated = {
            ...prev,
            ...updates,
          };
          console.log('Updated local state:', {
            bookId: updated.id,
            hasSpreadData: !!updated.spreads,
            spreadIds: Object.keys(updated.spreads || {}),
          });
          return updated;
        });
      } catch (error) {
        console.error('Firebase Update Error:', {
          message: error.message,
          code: error.code,
        });
        setError(error.message);
        throw error;
      }
    },
    [db, setCurrentBook]
  );

  // Lightweight setter: update only a single spread in local state.
  // Firestore write is handled by the caller (useSpreadElements).
  const updateSpread = useCallback((spreadId, spreadData) => {
    setCurrentBook((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        spreads: {
          ...(prev.spreads || {}),
          [spreadId]: spreadData,
        },
      };
    });
  }, []);

  // Clear book data when user logs out
  useEffect(() => {
    if (!user) {
      setCurrentBook(null);
      setError(null);
    }
  }, [user]);

  // Debug currentBook whenever it changes
  useEffect(() => {
    if (currentBook) {
      console.log('[SBW:BookContext] Current book updated:', {
        id: currentBook.id,
        totalPages: currentBook.totalPages,
        elementCount: currentBook.elements?.length,
        imageElements:
          currentBook.elements?.filter((el) => el.type === 'image')?.length ||
          0,
        imagePrompts:
          currentBook.elements?.filter(
            (el) => el.type === 'image' && el.imagePrompt
          )?.length || 0,
      });
    }
  }, [currentBook]);

  useEffect(() => {
    currentBookRef.current = currentBook;
    if (currentBook) {
      mergedFieldsRef.current = {
        world: currentBook.world,
        characterData: currentBook.characterData,
      };
    }
  }, [currentBook]);

  // Calculate if the book has actual spreads with content
  const hasActualSpreads = useMemo(() => {
    if (!currentBook || !currentBook.spreads) return false;
    
    // Check if there are any spreads keys
    const spreadIds = Object.keys(currentBook.spreads);
    if (spreadIds.length === 0) return false;
    
    // Check if any spread has elements with content
    return spreadIds.some(spreadId => {
      const spread = currentBook.spreads[spreadId];
      return spread && 
             spread.elements && 
             spread.elements.length > 0 &&
             spread.elements.some(element => 
               (element.type === 'text' && element.content?.trim?.()) || 
               (element.type === 'image' && (element.imageSrc || element.imagePrompt))
             );
    });
  }, [currentBook]);
  
  console.log('[SBW:BookContext] Has actual spreads with content:', hasActualSpreads);

  // Setup processing status listener when book changes
  useEffect(() => {
    let unsubscribe = null;
    // Guard to avoid repeated loadBook calls for the same status
    let lastStatus = null;
    
    if (currentBook?.id) {
      console.log('[SBW:BookContext] Setting up processing status listener for book:', currentBook.id);
      unsubscribe = onSnapshot(
        doc(db, "bookWithPreviewProcessing", currentBook.id),
        (doc) => {
          if (doc.exists()) {
            const data = doc.data();
            const status = data.status;
            const updatedAtDate = data.updatedAt?.toDate?.() || (data.updatedAt ? new Date(data.updatedAt) : null);
            setProcessingUpdatedAt(updatedAtDate && !isNaN(updatedAtDate.getTime()) ? updatedAtDate.getTime() : null);
            
            // Skip stale error/completed/cancelled documents (older than 5 minutes)
            if (status === 'error' || status === 'completed' || status === 'cancelled') {
              const updatedAt = updatedAtDate;
              if (updatedAt && (Date.now() - updatedAt.getTime()) > 5 * 60 * 1000) {
                console.log('[SBW:BookContext] Ignoring stale processing doc:', { status, updatedAt: updatedAt.toISOString() });
                setProcessingStatus(null);
                setShowImageLoadingPlaceholder(false);
                setIsDesignReadyForViewing(true);
                return;
              }
            }
            
            console.log('[SBW:BookContext] Processing status update:', {
              status,
              bookId: currentBook.id,
              timestamp: new Date().toISOString(),
              data: {
                error: data.error,
                errorDetails: data.errorDetails,
                createdAt: data.createdAt,
                updatedAt: data.updatedAt
              }
            });
            setProcessingStatus(status);
            
            // Use dynamic message from backend if available, otherwise use default
            if (data.loadingUIMessage) {
              console.log('[SBW:BookContext] Using dynamic loading message from backend:', data.loadingUIMessage);
              setLoadingUIMessage(data.loadingUIMessage);
            }
            
            // Only trigger heavy actions when status actually changes
            const statusChanged = status !== lastStatus;
            if (statusChanged) {
              lastStatus = status;
            }
            switch (status) {
              case "pending":
              case "pending2":
              case "processing":
              case "received":
              case "initializing":
              case "savingTextElements":
                console.log('this is it status',data?.status)
              case "updatingTextElements":
                // Reset any stale progress left over from a previous generation run
                if (!data.loadingUIMessage) {
                  setLoadingUIMessage({progress: 5, message: 'Preparing your design...'});
                }
                setShowImageLoadingPlaceholder(true);
                setAllowedProgress(1);
                setIsDesignReadyForViewing(false);
                console.log('this is it firing')
                break;
              case "updatingTextElements2":
                // Early status used by updateBookWithPreviewTwoHttp once the cover is saved
                // Show the image placeholder while interior elements are being prepared
                setLoadingUIMessage({progress: 30, message: 'Updating Text Elements...'});
                setShowImageLoadingPlaceholder(true);
                setAllowedProgress(1);
                setIsDesignReadyForViewing(true);
                if (!location.pathname.startsWith('/world') && statusChanged) loadBook(currentBook.id);
                break;
              case "generatingCharacter":
                setLoadingUIMessage({progress:20,message:'Generating Character...'});
                setShowImageLoadingPlaceholder(true);
                console.log('this is it status generating character')
                // Avoid reloading on World page to keep attributes stable
                if (!location.pathname.startsWith('/world')) {
                  if (!didReloadForGeneratingCharacterRef.current[currentBook.id]) {
                    loadBook(currentBook.id);
                    didReloadForGeneratingCharacterRef.current[currentBook.id] = true;
                  }
                }
                break;
              case "generatingImagePrompts":
                setLoadingUIMessage({progress:40,message:'Generating Image Prompts...'});
                setShowImageLoadingPlaceholder(true);
                console.log(' this is it status generating image prompts')
                if (!location.pathname.startsWith('/world') && statusChanged) loadBook(currentBook.id);
                break;
              case "generatingCoverImage":
                setLoadingUIMessage({progress:60,message:'Generating Cover Image...'});
                setShowImageLoadingPlaceholder(true);
                console.log('this is it status generating cover image')
                break;
              case "updatingImageElements":
                setLoadingUIMessage({progress:80,message:'Updating Image Elements...'});
                setShowImageLoadingPlaceholder(true);
                console.log('this is it updating image elements')
                setAllowedProgress(2);
                setIsDesignReadyForViewing(true);
                if (!location.pathname.startsWith('/world') && statusChanged) loadBook(currentBook.id);
                break;
              case "updatedTextElementsInterior":
                setLoadingUIMessage({progress:80,message:'Updating Text Elements...'});
                setShowImageLoadingPlaceholder(true);
                setAllowedProgress(1);
                console.log('this is it status updating text elements there should be texts on the other pages by now')
                setIsDesignReadyForViewing(true);
                if (!location.pathname.startsWith('/world') && statusChanged) loadBook(currentBook.id);
                break;
              case "updatedCoverImage":
                setLoadingUIMessage({progress:90,message:'Updating Text Elements...'});
                setShowImageLoadingPlaceholder(true);
                setAllowedProgress(1);
                console.log('this is it status updating text elements there should be a cover page now')
                setIsDesignReadyForViewing(true);
                if (!location.pathname.startsWith('/world') && statusChanged) loadBook(currentBook.id);
                break;
              case "completed":
                setShowImageLoadingPlaceholder(false);
                setAllowedProgress(4);
                if (!location.pathname.startsWith('/world') && statusChanged) loadBook(currentBook.id);
                setIsDesignReadyForViewing(true);
                break;
              case "error":
                // Handle error state - dismiss loading UI and show error
                console.error('[SBW:BookContext] Processing encountered an error:', data.error);
                setShowImageLoadingPlaceholder(false);
                setIsDesignReadyForViewing(true);
                setAllowedProgress(4);
                // Don't reload book on error - keep current state for user to see
                break;
              case "cancelled":
                // User cancelled a stale/stuck design generation - dismiss loading UI
                console.log('[SBW:BookContext] Processing was cancelled by user');
                setShowImageLoadingPlaceholder(false);
                setIsDesignReadyForViewing(true);
                setAllowedProgress(4);
                break;
            }
          } else {
            // If no processing document exists, consider it completed
            setProcessingStatus(null);
            setProcessingUpdatedAt(null);
            setShowImageLoadingPlaceholder(false);
            setIsDesignReadyForViewing(true);
          }
        },
        (error) => {
          console.error('[SBW:BookContext] Processing status listener error:', error);
          // Default to allowing navigation if there's an error
          setIsDesignReadyForViewing(true);
        }
      );
    } else {
      setProcessingStatus(null);
      setProcessingUpdatedAt(null);
      setIsDesignReadyForViewing(true);
      setAllowedProgress(3);
    }
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [currentBook?.id]);

  // Cancel a stale/stuck design generation by marking the processing doc as cancelled.
  // The processing status listener above will pick up the change and dismiss all loading UIs.
  const cancelDesignProcessing = useCallback(async () => {
    const bookId = currentBookRef.current?.id;
    if (!bookId) return;
    try {
      await updateDoc(doc(db, 'bookWithPreviewProcessing', bookId), {
        status: 'cancelled',
        cancelledAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
      console.log('[SBW:BookContext] Cancelled design processing for book:', bookId);
    } catch (e) {
      console.error('[SBW:BookContext] Failed to cancel design processing:', e);
      // Fall back to clearing local state so the user isn't stuck behind the overlay
      setProcessingStatus(null);
      setShowImageLoadingPlaceholder(false);
      setIsDesignReadyForViewing(true);
    }
  }, []);

  // Realtime: merge live book fields (world) into currentBook
  useEffect(() => {
    if (!currentBook?.id) return undefined;
    const ref = doc(db, 'books', currentBook.id);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      // We allow merging world so newly generated objects appear without hard refresh.

      const d = snap.data() || {};
      // Don't set showImageLoadingPlaceholder based on book.status
      // Preview generation (bookWithPreviewProcessing) and book processing are separate concerns
      // showImageLoadingPlaceholder should only be controlled by bookWithPreviewProcessing listener
      try {
        const bookStatus = d.status || null;
        // Only update design readiness, don't touch loading UI
        if (bookStatus === 'completed') {
          setIsDesignReadyForViewing(true);
        }
      } catch (_) {}
      const next = {};
      let changed = false;
      if (typeof d.world !== 'undefined') {
        if (d.world !== mergedFieldsRef.current.world) {
          next.world = d.world;
          changed = true;
        }
      } else if (typeof mergedFieldsRef.current.world !== 'undefined') {
        next.world = null;
        changed = true;
      }
      // Merge characterData too — WorldPage derives its objects from it, and without this
      // merge new world objects don't appear on /world until a hard refresh
      if (typeof d.characterData !== 'undefined') {
        if (d.characterData !== mergedFieldsRef.current.characterData) {
          next.characterData = d.characterData;
          changed = true;
        }
      }
      // Realtime spreads merge: if backend updates nested spreads (e.g., webhook writes new image src),
      // merge only the incoming spread keys to avoid clobbering local state.
      // CRITICAL FIX: Skip merging the currently active spread to preserve unsaved edits
      if (d.spreads && typeof d.spreads === 'object') {
        const incomingSpreadIds = Object.entries(d.spreads || {}).map(([spreadId, spread]) => ({
          spreadId,
          elementCount: spread?.elements?.length || 0,
          elements: spread?.elements?.map(el => ({
            id: el.id,
            type: el.type,
            orientation: el.orientation,
          })) || []
        }));
        const activeSpread = activeSpreadRef.current;
        const spreadsToMerge = incomingSpreadIds.filter(s => s.spreadId !== activeSpread);
        console.log('[BookContext] Real-time spreads update received:', {
          incomingSpreads: incomingSpreadIds,
          activeSpread,
          spreadsToMerge: spreadsToMerge.map(s => s.spreadId),
          skippedSpreads: incomingSpreadIds.filter(s => s.spreadId === activeSpread).map(s => s.spreadId)
        });
        setCurrentBook((prev) => {
          const prevSpreads = (prev && prev.spreads) || {};
          const incomingSpreads = d.spreads || {};
          // Only merge spreads that are NOT currently being edited
          const keysToMerge = Object.keys(incomingSpreads).filter(sid => sid !== activeSpreadRef.current);
          // Fast path: if no keys to merge, keep as is
          if (keysToMerge.length === 0) return { ...(prev || {}), ...(changed ? next : {}) };
          const mergedSpreads = { ...prevSpreads };
          keysToMerge.forEach((sid) => {
            const incoming = incomingSpreads[sid];
            if (!incoming) return;
            mergedSpreads[sid] = { ...(prevSpreads[sid] || {}), ...incoming };
          });
          return { ...(prev || {}), ...(changed ? next : {}), spreads: mergedSpreads };
        });
        // We already applied setCurrentBook for spreads; clear changed flag to avoid double set below
        changed = false;
      }
      if (changed) {
        console.log('this is it book onSnapshot merged fields', Object.keys(next));
        mergedFieldsRef.current = {
          world:
            typeof next.world !== 'undefined' ? next.world : mergedFieldsRef.current.world,
          characterData:
            typeof next.characterData !== 'undefined' ? next.characterData : mergedFieldsRef.current.characterData,
        };
        setCurrentBook((prev) => ({ ...(prev || {}), ...next }));
      }
    });
    return unsub;
  }, [currentBook?.id, location.pathname]);

  // Real-time listener for in-progress design image jobs
  useEffect(() => {
    if (!currentBook?.id) {
      setPendingImageJobs(new Map());
      setPendingImageJobsLoaded(false);
      return;
    }

    const jobsQuery = query(
      collection(db, 'imageUpdateJobs'),
      where('bookId', '==', currentBook.id),
      where('status', 'in', ['pending', 'received', 'retrying'])
    );

    setPendingImageJobsLoaded(false);

    const unsubscribe = onSnapshot(
      jobsQuery,
      (snapshot) => {
        const jobsMap = new Map();
        snapshot.docs.forEach((d) => {
          jobsMap.set(d.id, { id: d.id, ...d.data() });
        });
        setPendingImageJobs(jobsMap);
        setPendingImageJobsLoaded(true);
      },
      (err) => {
        console.error('[BookContext] Error listening to imageUpdateJobs:', err);
        // Mark as loaded so elements waiting on pendingImageJobsLoaded don't
        // stay hidden forever when the listener can't be established
        setPendingImageJobsLoaded(true);
      }
    );

    return () => unsubscribe();
  }, [currentBook?.id]);

  // Whether any image generation jobs are currently in flight for this book
  const hasActiveImageGeneration = pendingImageJobs.size > 0;

  // Count of image elements still generating across the whole book
  const pendingImageCount = pendingImageJobs.size;

  const value = {
    currentBook,
    loading,
    error,
    loadBook,
    clearBook,
    updateBook,
    updateSpread,
    setActiveSpread,
    hasActualSpreads,
    processingStatus,
    processingUpdatedAt,
    setProcessingStatus,
    cancelDesignProcessing,
    setLoadingUIMessage,
    isDesignReadyForViewing,
    allowedProgress,
    loadingUIMessage,
    showImageLoadingPlaceholder,
    hasActiveImageGeneration,
    pendingImageCount,
    pendingImageJobsLoaded,
    pendingImageJobs
  };

  return <BookContext.Provider value={value}>{children}</BookContext.Provider>;
}

export function useBook() {
  const context = useContext(BookContext);
  if (!context) {
    throw new Error('useBook must be used within a BookProvider');
  }
  return context;
}
